import React, { useState, useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { spacing, breakpoints, fontStyle, themeLight, borderRadius } from '@naf/theme';
import { Close } from '@styled-icons/material/Close';
import { Text, TextVariant } from '@naf/text';
import { propValidationConfig } from './propValidationConfig';
export { DialogIE } from './dialogIE';
export const Dialog = /*#__PURE__*/React.memo(({
  isOpen = false,
  size = 'default',
  children,
  button,
  handleClose,
  title,
  closeIcon = true,
  img,
  trapFocus = true,
  className
}) => {
  const [mouseDown, setMouseDown] = useState(false);
  useEffect(() => {
    if (isOpen && trapFocus) {
      const element = document.querySelector('#dialog');
      if (element) {
        let focusables = element.querySelectorAll('a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), iframe, object, [tabindex="0"], [contenteditable]');
        const firstFocusable = focusables[0];
        const lastFocusable = focusables[focusables.length - 1];
        firstFocusable.focus();
        const trapFocus = e => {
          if (e.key === 'Tab') {
            if (e.shiftKey && document.activeElement === firstFocusable) {
              /* shift + tab */
              lastFocusable.focus();
              e.preventDefault();
            } else if (document.activeElement === lastFocusable) {
              /* tab */
              firstFocusable.focus();
              e.preventDefault();
            }
          }
        };
        document.addEventListener('keydown', trapFocus);
        return () => document.removeEventListener('keydown', trapFocus);
      }
      return () => {};
    }
    return () => {};
  }, [isOpen, trapFocus]);
  const removeOutline = () => {
    setMouseDown(true);
  };
  const addOutline = e => {
    if (e.key === 'Tab') {
      setMouseDown(false);
    }
  };
  useEffect(() => {
    window.addEventListener('mousedown', removeOutline);
    return () => window.removeEventListener('mousedown', removeOutline);
  }, []);
  const close = useCallback(e => {
    if (e.key === 'Escape') {
      handleClose();
    }
  }, [handleClose]);
  useEffect(() => {
    window.addEventListener('keydown', e => {
      close(e);
      addOutline(e);
    });
    return () => window.removeEventListener('keydown', e => {
      close(e);
      addOutline(e);
    });
  }, [close]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, isOpen && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Overlay, {
    onClick: () => handleClose()
  }), /*#__PURE__*/React.createElement(ContentWrapper, {
    id: "dialog"
  }, /*#__PURE__*/React.createElement(Container, {
    className: className,
    id: mouseDown ? 'mousedown' : '',
    size: size,
    "aria-modal": isOpen
  }, /*#__PURE__*/React.createElement(Header, null, closeIcon && /*#__PURE__*/React.createElement("button", {
    onClick: () => handleClose(),
    "aria-label": "Lukk dialogboks"
  }, /*#__PURE__*/React.createElement(Close, {
    size: 24
  }))), /*#__PURE__*/React.createElement(Inner, null, img && /*#__PURE__*/React.createElement("img", {
    src: img,
    alt: "Illustrasjon"
  }), title && /*#__PURE__*/React.createElement(Text, {
    variant: TextVariant.Header3
  }, title), children), button && /*#__PURE__*/React.createElement(Footer, null, button)))));
});
export default Dialog;
const ContentWrapper = styled.div.withConfig({
  displayName: "dialog__ContentWrapper",
  componentId: "sc-f4uqye-0"
})([""]);
const Overlay = styled.div.withConfig({
  displayName: "dialog__Overlay",
  componentId: "sc-f4uqye-1"
})(["position:fixed;top:0;left:0;width:100%;height:100%;background-color:'rgba(6, 46, 42, 0.5)';z-index:99;animation:opacity 0.25s;@keyframes opacity{from{opacity:0;}to{opacity:1;}}"]);
const Container = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "dialog__Container",
  componentId: "sc-f4uqye-2"
})(["display:flex;flex-wrap:wrap;background-color:", ";position:fixed;top:50%;left:50%;overflow-y:auto;z-index:999;box-shadow:0px 0px 4px 0px rgba(0,0,0,0.15);transform:translate(-50%,-50%);", ";padding:0 0 ", ";border-radius:", ";animation:opacity 0.25s;@keyframes opacity{from{opacity:0;}to{opacity:1;}}", " ", " @media (max-width:", "){width:100%;position:fixed;bottom:0;left:0;top:auto;max-height:100%;transform:none;", ";padding:0 0 ", ";}&#mousedown *:focus{outline:0;}"], ({
  theme
}) => theme.background ? theme.background.default : themeLight.background.default, fontStyle.article.articleText, spacing.space48, borderRadius.M, ({
  size
}) => size === 'default' && css(["max-width:560px;max-height:72%;"]), ({
  size
}) => size === 'small' && css(["max-width:420px;max-height:48%;"]), breakpoints.s, fontStyle.bodyText.bodyText, spacing.space40);
const Header = styled.div.withConfig({
  displayName: "dialog__Header",
  componentId: "sc-f4uqye-3"
})(["flex-basis:100%;height:", ";display:flex;justify-content:flex-end;button{margin-right:", ";margin-top:", ";padding:0;border:none;background-color:inherit;touch-action:manipulation;height:", ";color:", " !important;&:hover{cursor:pointer;}}"], spacing.space48, spacing.space16, spacing.space16, spacing.space24, themeLight.typography.brandText);
const Inner = styled.div.withConfig({
  displayName: "dialog__Inner",
  componentId: "sc-f4uqye-4"
})(["margin:0 ", ";display:flex;flex-direction:column;img{margin-bottom:", ";max-width:50%;object-fit:contain;border-radius:", ";@media (max-width:", "){margin-bottom:", ";}}h3{margin:0 0 ", ";}@media (max-width:", "){margin:0 ", ";}"], spacing.space48, spacing.space32, borderRadius.S, breakpoints.s, spacing.space12, spacing.space16, breakpoints.s, spacing.space24);
const Footer = styled.div.withConfig({
  displayName: "dialog__Footer",
  componentId: "sc-f4uqye-5"
})(["flex-basis:100%;margin:", " ", " 0;button{margin-right:", ";border-radius:", ";}@media (max-width:", "){margin:", " ", " 0;}"], spacing.space32, spacing.space48, spacing.space16, borderRadius.L, breakpoints.s, spacing.space32, spacing.space24);
//# sourceMappingURL=dialog.js.map